<template>
  <div
    :class="{
      'otp-input': true,
      'has-errors': errors,
      'code-sent': codeSent,
      complete: isComplete,
    }"
  >
    <input
      placeholder="0"
      v-for="(digit, index) in digits"
      :key="index"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      ref="otpInput"
      class="input"
      @input="handleInput($event, index)"
      @keydown.backspace="handleBackspace($event, index)"
      @paste="handlePaste"
      v-on:keyup.enter="handleEnter"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";

const props = defineProps({
  numInputs: {
    type: Number,
    default: 6,
  },
  errors: {
    type: Boolean,
    default: false,
  },
  codeSent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["onChange", "pressedEnter"]);
const digits = ref(Array(props.numInputs).fill(""));
const isComplete = computed(() => digits.value.every((digit) => digit !== ""));

const handleEnter = () => {
  emit("pressedEnter");
};

const handlePaste = (event) => {
  const pasteData = event.clipboardData
    .getData("text")
    .replace(/\D/g, "")
    .trim();
  if (pasteData.length === props.numInputs && /^\d+$/.test(pasteData)) {
    const inputs = event.target.parentNode.querySelectorAll("input");

    pasteData.split("").forEach((char, index) => {
      digits.value[index] = char;
      inputs[index].value = char; // Directly update the DOM input value
    });

    // Focus the last filled input
    const lastFilledIndex = Math.min(pasteData.length - 1, props.numInputs - 1);
    inputs[lastFilledIndex]?.focus();

    emit("onChange", digits.value.join(""));
    emit("pressedEnter");
    event.preventDefault();
  }
};

// Method to handle input and jump to the next box, rejecting non-numeric input
const handleInput = (event, index) => {
  const inputVal = event.target.value;
  if (inputVal.length > 1) {
    event.clipboardData = {
      getData: () => inputVal.trim(),
    };
    handlePaste(event);
    return;
  }

  // Check if the input is a number, if not, reset the value
  if (!/^\d$/.test(inputVal)) {
    event.target.value = ""; // Clear the input if it's not a number
    digits.value[index] = ""; // Clear the corresponding digit
    return;
  }

  digits.value[index] = inputVal;
  emit("onChange", digits.value.join(""));

  if (inputVal && index < props.numInputs - 1) {
    // Move to the next input box
    event.target.nextElementSibling?.focus();
  }

  if (digits.value.join("").length === props.numInputs) {
    emit("pressedEnter");
  }
};

const handleBackspace = (event, index) => {
  if (!digits.value[index] && index > 0) {
    // Move to the previous input box
    event.target.previousElementSibling?.focus();
  }
};

const otpInput = ref([]);

onMounted(() => {
  if (otpInput.value[0]) {
    otpInput.value[0].focus();
  }
});
</script>

<style scoped lang="scss">
@import "@/assets/styles/mixins.scss";

.otp-input {
  display: flex;
  justify-content: center;
  gap: 0.25em;

  @media (min-width: 768px) {
    gap: 1em;
  }

  input {
    width: 56px;
    height: 62px;
    border-radius: 8px;
    text-align: center;
    background-color: #f5f5f5;
    border: 1px solid transparent;
    outline: none;
    font-size: 30px;

    @include breakpoint(small) {
      font-size: 36px;
      width: 66px;
      height: 72px;
    }

    &:focus {
      // border-color: var(--primary-color);
    }
  }

  &.has-errors input {
    // border-color: #dc2626;
  }

  &.code-sent input {
    // border-color: #10b981;
  }

  &.complete input {
    // border-color: #10b981;
  }

  input::placeholder {
    color: #d4d4d4;
  }
}
</style>
